import {useAtom} from "jotai"
import {type CustomerAnalysisOptions, CustomerAnalysisOptionsAtom} from "./CustomerAnalysisOptions.ts";
import {resolveSetterVal, type Setter, type SetterVal} from "../../util/setter/Setter.ts";
import {CustomerProjectOptionsBlock} from "./CustomerProjectOptionsBlock.tsx";
import {useFieldSetter} from "../../util/setter/FieldSetter.ts";
import {Field} from "@matillion/component-library";
import type {ChangeEvent} from "react";
import {CheckboxField} from "../../components/checkbox-field/CheckboxField.tsx";
import {CustomerJobStrategyOptions} from "./CustomerJobStrategyOptions.tsx";
import {PromiseRunnerOptionsWidget} from "../../analysis-process/2-option-selection/PromiseRunnerOptionsWidget.tsx";

function TaskHistoryOptions({options, setOptions}: {options: CustomerAnalysisOptions["analyseTaskHistory"], setOptions: Setter<CustomerAnalysisOptions["analyseTaskHistory"]>}) {
    const [durationToQueryInDays, setDurationToQueryInDays] = useFieldSetter(options, setOptions, "durationToQueryInDays")
    const [taskQueryDurationHours, setTaskQueryDurationHours] = useFieldSetter(options, setOptions, "taskQueryDurationHours")
    const [runner, setRunner] = useFieldSetter(options, setOptions, "promiseRunner")
    return <div>
        <h3>Task History Scan</h3>
        <Field title="Duration to query in days"
               description="The total number of days to scan the task history of"
               type="number" value={durationToQueryInDays} onChange={(newValue: ChangeEvent<HTMLInputElement>) => setDurationToQueryInDays(newValue.target.valueAsNumber)} />
        <Field title="Task query duration hours"
               description="The number of hours to query in a single API request"
               type="number" value={taskQueryDurationHours} onChange={(newValue: ChangeEvent<HTMLInputElement>) => setTaskQueryDurationHours(newValue.target.valueAsNumber)} />
        <PromiseRunnerOptionsWidget title={"Task Download Runner"}
                                    description="Whether to download task history blocks in series, or in parallel"
                                    options={runner} setOptions={setRunner} />
    </div>
}

export function CustomerAnalysisOptionsWidget() {
    const [options, setOptions] = useAtom(CustomerAnalysisOptionsAtom)
    const safeSetOptions = (newOptions: SetterVal<CustomerAnalysisOptions>) => {
        setOptions((oldValue) => {
            const newVal = resolveSetterVal(oldValue, newOptions)
            if(newVal !== oldValue) {
                return {...newVal, id: Math.random().toString(36).slice(2, 9)}
            }
            return oldValue
        })
    }
    const [taskHistory, setTaskHistory] = useFieldSetter(options, safeSetOptions, "analyseTaskHistory")
    const [reportAnonymisedErrors, setReportAnonymisedErrors] = useFieldSetter(options, safeSetOptions, "reportAnonymisedErrors")
    
    return <div>
        <h2>Option Selection</h2>
        <div>
            <h3>Projects</h3>
            <CustomerProjectOptionsBlock options={options} setOptions={safeSetOptions} />
        </div>
        <hr />
        <div>
            <TaskHistoryOptions options={taskHistory} setOptions={setTaskHistory} />
        </div>
        <hr />
        <div>
            <h3>Error Reporting</h3>
            <CheckboxField title="Report Anonymised Errors"
                   description="Send anonymised error reports to help improve the analysis process"
                   small
                   value={reportAnonymisedErrors} setValue={setReportAnonymisedErrors} />
        </div>
        <hr />
        <div>
            <CustomerJobStrategyOptions options={options} setOptions={safeSetOptions} />
        </div>
    </div>
}